import React, { useState, useEffect, useMemo } from 'react'
import { api } from '../../api'
import SearchableSelect from '../SearchableSelect.jsx'
import { mergeCategoryMinimums, getAmcCategoryById, formatMinInvestment } from '../../data/mf_amc_categories'

const SIMPLE_SCHEME_CATEGORIES = ['SIF', 'PMS', 'AIF', 'GIFT_CITY_FUNDS']

export default function StepMFScheme({ onBack, onNext, token, initialAmcCategoryId = 'MF', initialAmcCode = '', initialSchemeCode = '', initialHasExistingFolio, initialFolioNumber = '' }) {
  const [categoryMinimumsMap, setCategoryMinimumsMap] = useState(null)
  const enrichedCategories = useMemo(
    () => mergeCategoryMinimums(categoryMinimumsMap || {}),
    [categoryMinimumsMap]
  )
  const [selectedAmcCategory, setSelectedAmcCategory] = useState(() =>
    getAmcCategoryById(initialAmcCategoryId, mergeCategoryMinimums({}))
  )
  const [amcs, setAmcs] = useState([])
  const [schemes, setSchemes] = useState([])
  const [selectedAmc, setSelectedAmc] = useState(null)
  const [selectedScheme, setSelectedScheme] = useState(null)
  const [hasExistingFolio, setHasExistingFolio] = useState(initialHasExistingFolio ?? null)
  const [folioNumber, setFolioNumber] = useState(initialFolioNumber)
  const [loading, setLoading] = useState(false)
  const [initialAmcApplied, setInitialAmcApplied] = useState(false)
  const [initialSchemeApplied, setInitialSchemeApplied] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await api.getCategoryMinimums()
        const m = data?.minimums && typeof data.minimums === 'object' ? data.minimums : {}
        if (!cancelled) setCategoryMinimumsMap(m)
      } catch (e) {
        console.error(e)
        if (!cancelled) setCategoryMinimumsMap({})
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    setSelectedAmcCategory((prev) =>
      getAmcCategoryById(prev?.id || initialAmcCategoryId, enrichedCategories)
    )
  }, [enrichedCategories, initialAmcCategoryId])

  useEffect(() => {
    loadAMCs()
  }, [token])

  useEffect(() => {
    if (selectedAmc?.amc_code) {
      loadSchemes(selectedAmc.amc_code)
    }
  }, [selectedAmc, selectedAmcCategory?.id])

  const loadAMCs = async () => {
    if (!token) return
    setLoading(true)
    try {
      const result = await api.listAMCs(token)
      setAmcs(Array.isArray(result) ? result : [])
    } catch (error) {
      console.error('Failed to load AMCs:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadSchemes = async (amc_code) => {
    if (!token || !amc_code) return
    setLoading(true)
    try {
      const result = await api.getSchemesByAMC(token, amc_code)
      setSchemes(Array.isArray(result) ? result : [])
    } catch (error) {
      console.error('Failed to load schemes:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (!initialAmcApplied && initialAmcCode && amcs.length > 0) {
      const amc = amcs.find(a => a.amc_code === initialAmcCode)
      if (amc) {
        setSelectedAmc(amc)
        setInitialAmcApplied(true)
      }
    }
  }, [initialAmcCode, amcs, initialAmcApplied])

  useEffect(() => {
    if (!initialSchemeApplied && initialSchemeCode && schemes.length > 0) {
      const scheme = schemes.find(s => s.scheme_code === initialSchemeCode)
      if (scheme) {
        setSelectedScheme(scheme)
        setInitialSchemeApplied(true)
      }
    }
  }, [initialSchemeCode, schemes, initialSchemeApplied])

  const amcMatchesCategory = (amc, catId) => {
    const ac =
      (amc?.amc_category && String(amc.amc_category)) ||
      (Array.isArray(amc?.categories) && amc.categories.length ? amc.categories[0] : null) ||
      'MF'
    return ac === catId
  }

  const amcOptions = useMemo(() => {
    const catId = selectedAmcCategory?.id || 'MF'
    return amcs
      .filter((amc) => amcMatchesCategory(amc, catId))
      .map((amc) => ({
        label: amc.amc_name,
        value: amc.amc_code
      }))
  }, [amcs, selectedAmcCategory?.id])

  const schemeOptions = useMemo(() => {
    return schemes.map((scheme) => ({
      label: `${scheme.display_name || scheme.scheme_name}${scheme.is_nfo ? ' [NFO]' : ''}`,
      value: scheme.scheme_code,
      scheme
    }))
  }, [schemes])

  const isSimpleSelectedCategory = SIMPLE_SCHEME_CATEGORIES.includes(selectedAmcCategory?.id || 'MF')

  const handleNext = () => {
    if (!selectedScheme || hasExistingFolio === null) return

    onNext({
      selectedAmcCategory,
      selectedAmc,
      selectedScheme,
      hasExistingFolio,
      folioNumber: hasExistingFolio ? folioNumber : null
    })
  }

  return (
    <div>
      <h3 className="mt-0 text-lg font-semibold text-gray-900 dark:text-gray-100">Step 4 — Select Mutual Fund Details</h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">Choose the AMC category, then AMC and Scheme, and indicate if you have an existing folio</p>
      
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            AMC Category <span className="text-red-500">*</span>
          </label>
          <div className="flex flex-wrap gap-2">
            {enrichedCategories.map((cat) => {
              const isSelected = selectedAmcCategory?.id === cat.id
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    setSelectedAmcCategory(cat)
                    setSelectedAmc(null)
                    setSelectedScheme(null)
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isSelected
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {cat.label} – {formatMinInvestment(cat.minInvestment)}
                </button>
              )
            })}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Select AMC (Asset Management Company) <span className="text-red-500">*</span>
          </label>
          <SearchableSelect
            options={amcOptions}
            value={selectedAmc?.amc_code || ''}
            onChange={(amcCode) => {
              const amc = amcs.find((a) => a.amc_code === amcCode)
              setSelectedAmc(amc || null)
              setSelectedScheme(null)
            }}
            placeholder="Search for an AMC..."
            disabled={loading}
            maxHeight={250}
          />
        </div>

        {selectedAmc && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Select Scheme <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              options={schemeOptions}
              value={selectedScheme?.scheme_code || ''}
              onChange={(schemeCode) => {
                const scheme = schemes.find((s) => s.scheme_code === schemeCode)
                setSelectedScheme(scheme || null)
              }}
              placeholder="Search for a scheme..."
              disabled={!selectedAmc || loading}
              maxHeight={300}
            />
            {selectedScheme && (
              <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                {selectedScheme.is_nfo && (
                  <div className="mb-3">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border border-yellow-300 dark:border-yellow-700">
                      NFO - New Fund Offer
                    </span>
                  </div>
                )}
                {!isSimpleSelectedCategory && (
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {(selectedAmcCategory?.id && selectedAmcCategory.id !== 'MF') && (
                      <div>
                        <span className="font-medium text-gray-700 dark:text-gray-300">AMC Category:</span>
                        <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedAmcCategory.label} ({formatMinInvestment(selectedAmcCategory.minInvestment)} min)</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Category:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedScheme.category}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Sub-Category:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedScheme.sub_category}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Plan:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedScheme.plan}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Option:</span>
                      <span className="ml-2">
                        {selectedScheme.option ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            selectedScheme.option === 'GROWTH' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                            selectedScheme.option === 'IDCW_PAYOUT' ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300' :
                            'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300'
                          }`}>
                            {selectedScheme.option === 'GROWTH' ? 'Growth' : 
                             selectedScheme.option === 'IDCW_PAYOUT' ? 'IDCW – Payout' : 
                             selectedScheme.option === 'IDCW_REINVEST' ? 'IDCW – Reinvestment' : 
                             selectedScheme.option}
                          </span>
                        ) : (
                          <span className="text-gray-600 dark:text-gray-400">-</span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">{selectedScheme.type}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">CC %</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        {selectedScheme.cc !== undefined ? `${Number(selectedScheme.cc).toFixed(2)} %` : '0.00 %'}
                      </span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700 dark:text-gray-300">SI %</span>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        {selectedScheme.si !== undefined ? `${Number(selectedScheme.si).toFixed(2)} %` : '0.00 %'}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {selectedScheme && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Do you have an existing folio number? <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4 mb-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="hasExistingFolio"
                  value="yes"
                  checked={hasExistingFolio === true}
                  onChange={() => setHasExistingFolio(true)}
                  className="mr-2 w-4 h-4 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">Yes, I have an existing folio</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="hasExistingFolio"
                  value="no"
                  checked={hasExistingFolio === false}
                  onChange={() => setHasExistingFolio(false)}
                  className="mr-2 w-4 h-4 text-red-600 focus:ring-red-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">No, new folio</span>
              </label>
            </div>
            
            {hasExistingFolio && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Folio Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={folioNumber}
                  onChange={(e) => setFolioNumber(e.target.value)}
                  placeholder="Enter your folio number"
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="actions" style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
        <button onClick={onBack} className="appearance-none border border-gray-200 dark:border-gray-700 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 bg-white/85 dark:bg-gray-800/85 font-bold text-gray-900 dark:text-gray-100 hover:bg-white dark:hover:bg-gray-800 transition-colors text-sm sm:text-base">
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={!selectedScheme || hasExistingFolio === null || (hasExistingFolio && !folioNumber)}
          className="appearance-none border border-gray-200 dark:border-gray-700 rounded-full px-4 py-2.5 sm:px-5 sm:py-3 font-bold bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-700 text-gray-900 dark:text-gray-100 cursor-pointer hover:shadow-md transition-shadow disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          Continue
        </button>
      </div>
    </div>
  )
}
